import React, { useEffect, useState } from 'react';
import api from '../../../api';
import AttendanceAddModal from './modals/Attendance/AttendanceAddModal';
import SpecialityDropdown from '../tabs/Dropdown/SpecialityDropdown';
import Pagination from '../Tools/Pagination';

const AttendanceTab = () => {
  // State for attendance data and filtered results
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);

  // State for modal and form
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    emp_id: '',
    todaystatus: '1',
    date: new Date().toISOString().split('T')[0],
    check_in: '',
    check_out: '',
    notes: ''
  });

  // State for edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentAttendanceId, setCurrentAttendanceId] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Filter states
  const [filters, setFilters] = useState({
    emp_id: '',
    speciality: '',
    status: ''
  });

  /**
   * Fetch attendance data from API
   */
  const fetchAttendance = async () => {
    try {
      setLoading(true);
      const response = await api.get('/attendance');
      setAttendanceData(response.data || []);
      setFilteredData(response.data || []);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      alert('Failed to fetch attendance data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Apply filters to attendance data
   */
  const applyFilters = () => {
    let data = [...attendanceData];

    // Filter by employee ID
    if (filters.emp_id) {
      data = data.filter(d => 
        d.emp_id?.toString().includes(filters.emp_id)
      );
    }

     // Filter by speciality
    if (filters.speciality) {
      data = data.filter(d => 
        d.employee?.speciality?.speciality_name === filters.speciality
      );
    }

     // Filter by status
    if (filters.status) {
      if (filters.status === 'Present') {
        data = data.filter(d => d.todaystatus === 1);
      } else if (filters.status === 'Absent') {
        data = data.filter(d => d.todaystatus === 2);
      }
    }

    setFilteredData(data);
    setCurrentPage(1);
  };

  /**
   * Handle filter input changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Reset all filters
   */
  const handleClearFilters = () => {
    setFilters({ emp_id: '', speciality: '', status: '' });
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Prepare form for editing an attendance record
   */
  const handleEditAttendance = (attendance) => {
  setIsEditMode(true);
  setCurrentAttendanceId(attendance.id);
  setFormData({
    emp_id: attendance.emp_id,
    todaystatus: attendance.todaystatus?.toString() || '0', // Convert to string, default to '0' (Not Mark)
    date: attendance.date || new Date().toISOString().split('T')[0],
    check_in: attendance.check_in || '',
    check_out: attendance.check_out || '',
    notes: attendance.notes || ''
  });
  setShowModal(true);
};
  /**
   * Save attendance record (create or update)
   */
  const handleSave = async () => {
  try {
    const payload = {
      emp_id: formData.emp_id,
      todaystatus: parseInt(formData.todaystatus), // Convert to number
      date: formData.date,
      check_in: formData.check_in,
      check_out: formData.check_out,
      notes: formData.notes
    };

    // Remove todaystatus if it's 0 (Not Mark)
    if (payload.todaystatus === 0) {
      delete payload.todaystatus;
    }

    if (isEditMode) {
      await api.put(`/attendance/${currentAttendanceId}`, payload);
      alert('Attendance record updated successfully!');
    } else {
      await api.post('/attendance', payload);
      alert('Attendance record added successfully!');
    }

    fetchAttendance();
    setShowModal(false);
  } catch (error) {
    console.error('Error saving attendance:', error);
    alert(`Failed to ${isEditMode ? 'update' : 'add'} attendance record`);
  }
};

  /**
   * Delete an attendance record
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this attendance record?')) {
      try {
        await api.delete(`/attendance/${id}`);
        alert('Attendance record deleted successfully!');
        fetchAttendance();
      } catch (error) {
        console.error('Error deleting attendance:', error);
        alert('Failed to delete attendance record');
      }
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchAttendance();
  }, []);

  // Apply filters when filters or attendanceData changes
  useEffect(() => {
    applyFilters();
  }, [filters, attendanceData]);

  // Pagination calculations
  const totalItems = filteredData.length;
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="tab-pane active" id="employee" role="tabpanel">
      {/* Filter Section */}
      <div className="filter-search mt-3">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            {/* Employee ID Filter */}
            <div className="form-group col-lg-3 col-12 p-2">
              <select
                className="form-control"
                name="emp_id"
                value={filters.emp_id}
                onChange={handleFilterChange}
              >
                <option value="">Select Employee</option>
                {attendanceData.map((record) => (
                  <option key={record.id} value={record.emp_id}>
                    {record.emp_id}
                  </option>
                ))}
              </select>
            </div>

            {/* Speciality Filter */}
            <div className="form-group col-lg-3 col-12 p-2">
              <SpecialityDropdown
                name="speciality"
                value={filters.speciality}
                onChange={handleFilterChange}
              />
            </div>

            {/* Status Filter */}
            <div className="form-group col-lg-3 col-12 p-2">
              <select
                className="form-control form-select"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Select Status</option>
                <option>Present</option>
                <option>Absent</option>
              </select>
            </div>

            {/* Clear Filters Button */}
            <div className="form-group col-lg-3 col-12 p-2">
              <button
                type="button"
                className="btn mb-1 btn-outline-primary btn-rounded"
                onClick={handleClearFilters}
              >
                Clear Filter
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="py-3 px-0">
        {loading ? (
          <div className="text-center py-4">Loading attendance...</div>
        ) : (
          <>
            <div className="table-responsive rounded-2 ros-table">
              <table className="table text-nowrap customize-table mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th><h6 className="fw-semibold mb-0">Employee ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Speciality</h6></th>
                    <th><h6 className="fw-semibold mb-0">Status</h6></th>
                    <th><h6 className="fw-semibold mb-0">Date</h6></th>
                    <th><h6 className="fw-semibold mb-0">Actions</h6></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((record) => (
                      <tr key={record.id}>
                        <td>{record.emp_id || 'EMP-000'}</td>
                        <td>{record.employee?.speciality?.speciality_name || 'Null'}</td>
                        <td>
                          <span className={`round-8 rounded-circle me-2 d-inline-block ${
                            record.todaystatus === 1 ? 'text-bg-success' : 
                            record.todaystatus === 2 ? 'text-bg-danger' : 'text-bg-secondary'
                          }`}></span>
                          {record.todaystatus === 0 ? 'Not Mark' : 
                           record.todaystatus === 1 ? 'Present' : 'Absent'}
                        </td>
                        <td>{record.created_at}</td>
                        <td>
                          <button 
                            className="h4 me-3 border-0 bg-transparent" 
                            onClick={() => handleEditAttendance(record)}
                            title="Edit"
                          >
                            <i className="ti ti-edit text-warning"></i>
                          </button>
                          <button 
                            className="h4 border-0 bg-transparent" 
                            onClick={() => handleDelete(record.id)}
                            title="Delete"
                          >
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4">
                        No attendance records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              total={totalItems}
              limit={itemsPerPage}
              page={currentPage}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>

      {/* Attendance Add/Edit Modal */}
      <AttendanceAddModal
        show={showModal}
        onClose={() => setShowModal(false)}
        formData={formData}
        onChange={handleInputChange}
        onSave={handleSave}
        isEditMode={isEditMode}
      />
    </div>
  );
};

export default AttendanceTab;